import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../api/client.js';
import type {
  IScan,
  IQuickWin,
  IEvaluationResult,
  IActiveScanStatus,
  IScanRecommendation,
} from '../../shared/types.js';

/** Auswahl der Quick Wins: eigene (manual) oder Systemauswahl; selectable = Spalte vorhanden. */
export interface QuickWinSelectionState {
  manual: boolean;
  selectable: boolean;
}

interface ScanDetail {
  scan: IScan;
  evaluation: IEvaluationResult | null;
  quick_wins: IQuickWin[] | null;
  /** Konfigurator Plus: Handlungsempfehlungen der gefeuerten Faelle; null bei Legacy-Auswertungen. */
  recommendations?: IScanRecommendation[] | null;
  quick_win_selection?: QuickWinSelectionState | null;
  disclaimer: string | null;
}

interface Pagination {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
}

interface ScanState {
  scans: IScan[];
  pagination: Pagination;
  currentScan: ScanDetail | null;
  loading: boolean;
  polling: boolean;
  error: string | null;
  /**
   * Ergebnis des Smart-Polling-Endpoints `/scans/active-statuses`.
   * Wird im ArchivePage verwendet, um live Label-Chips zu aktualisieren.
   */
  activeStatuses: IActiveScanStatus[];
}

const initialState: ScanState = {
  scans: [],
  pagination: { page: 1, limit: 25, totalItems: 0, totalPages: 0 },
  currentScan: null,
  loading: false,
  polling: false,
  error: null,
  activeStatuses: [],
};

interface StartScanPayload {
  url: string;
  scan_template_id?: number;
}

export const startScan = createAsyncThunk('scan/start', async (payload: StartScanPayload, { rejectWithValue }) => {
  const res = await api.post<{ scan: IScan }>('/scans', payload);
  if (!res.success) return rejectWithValue(res.error.message);
  return res.data.scan;
});

export const fetchScan = createAsyncThunk('scan/fetch', async (id: number, { rejectWithValue }) => {
  const res = await api.get<ScanDetail>(`/scans/${id}`);
  if (!res.success) return rejectWithValue(res.error.message);
  return res.data;
});

export const reEvaluateScan = createAsyncThunk('scan/reEvaluate', async (id: number, { rejectWithValue }) => {
  const res = await api.post<{ evaluation: IEvaluationResult }>(`/scans/${id}/re-evaluate`, {});
  if (!res.success) return rejectWithValue(res.error.message);
  return res.data.evaluation;
});

interface FetchScansParams {
  page?: number;
  limit?: number;
  search?: string;
}

export const fetchActiveStatuses = createAsyncThunk(
  'scan/fetchActiveStatuses',
  async (_: void, { rejectWithValue }) => {
    const res = await api.get<{ scans: IActiveScanStatus[] }>('/scans/active-statuses');
    if (!res.success) return rejectWithValue(res.error.message);
    return res.data.scans;
  },
);

export const fetchScans = createAsyncThunk('scan/fetchAll', async (params: FetchScansParams = {}, { rejectWithValue }) => {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.search) qs.set('search', params.search);
  const query = qs.toString();
  const res = await api.get<{ scans: IScan[]; pagination: Pagination }>(`/scans${query ? `?${query}` : ''}`);
  if (!res.success) return rejectWithValue(res.error.message);
  return res.data;
});

const scanSlice = createSlice({
  name: 'scan',
  initialState,
  reducers: {
    clearCurrentScan(state) {
      state.currentScan = null;
    },
    setPolling(state, action: PayloadAction<boolean>) {
      state.polling = action.payload;
    },
    clearScanError(state) {
      state.error = null;
    },
    removeScan(state, action: PayloadAction<number>) {
      state.scans = state.scans.filter((s) => s.id !== action.payload);
    },
    /** Antwort von PUT /scans/:id/quick-wins uebernehmen. */
    setScanQuickWins(state, action: PayloadAction<{
      recommendations: IScanRecommendation[];
      quick_wins: IQuickWin[];
      quick_win_selection: QuickWinSelectionState;
    }>) {
      if (!state.currentScan) return;
      state.currentScan = { ...state.currentScan, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startScan.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(startScan.fulfilled, (state, action) => {
        state.loading = false;
        state.currentScan = { scan: action.payload, evaluation: null, quick_wins: null, disclaimer: null };
      })
      .addCase(startScan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchScan.pending, (state) => {
        state.loading = state.currentScan == null;
        state.error = null;
      })
      .addCase(fetchScan.fulfilled, (state, action) => {
        state.currentScan = action.payload;
        state.loading = false;
      })
      .addCase(fetchScan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(reEvaluateScan.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(reEvaluateScan.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentScan) {
          state.currentScan = { ...state.currentScan, evaluation: action.payload };
        }
      })
      .addCase(reEvaluateScan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchScans.pending, (state) => { state.loading = true; })
      .addCase(fetchScans.fulfilled, (state, action) => {
        state.scans = action.payload.scans;
        state.pagination = action.payload.pagination;
        state.loading = false;
      })
      .addCase(fetchScans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchActiveStatuses.fulfilled, (state, action) => {
        state.activeStatuses = action.payload;
        // Archiv-Liste inline patchen, damit neue Labels ohne reloadScans durchschlagen.
        if (state.scans.length > 0 && action.payload.length > 0) {
          const byId = new Map(action.payload.map((s) => [s.id, s.scan_label]));
          state.scans = state.scans.map((scan) =>
            byId.has(scan.id) ? { ...scan, scan_label: byId.get(scan.id) ?? scan.scan_label } : scan,
          );
        }
      })
      .addCase(fetchActiveStatuses.rejected, (state) => {
        // Silent: Netzwerkfehler soll keinen Toast ausloesen (PRD 6: Smart Polling).
        state.activeStatuses = state.activeStatuses;
      });
  },
});

export const { clearCurrentScan, setPolling, clearScanError, removeScan, setScanQuickWins } = scanSlice.actions;
export default scanSlice.reducer;
